/**
 * Shared constants, formatters, and config for the Payments Report module.
 */
import { format, startOfMonth, startOfWeek, subMonths, startOfYear } from "date-fns";
import { Banknote, CreditCard, Receipt, Smartphone, FileText, Landmark, ArrowDownLeft, Building } from "lucide-react";

export const API = process.env.REACT_APP_BACKEND_URL;

export const formatCurrency = (v) => {
  if (!v && v !== 0) return "\u20B90";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v);
};

export const formatCompact = (v) => {
  if (!v) return "\u20B90";
  if (v >= 10000000) return `\u20B9${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `\u20B9${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `\u20B9${(v / 1000).toFixed(1)}K`;
  return `\u20B9${v.toLocaleString("en-IN")}`;
};

export const formatDateIST = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr + "T00:00:00+05:30");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
  } catch { return dateStr; }
};

export const MODE_COLORS = {
  Cash: "#059669", Card: "#2563eb", Credit: "#4f46e5", "Self Credit": "#6d28d9",
  UPI: "#7c3aed", EFT: "#d97706", "NEFT/RTGS": "#0891b2", Advance: "#f59e0b", Payout: "#dc2626", Others: "#ea580c",
};
export const CHART_COLORS = Object.values(MODE_COLORS);

export const DATE_PRESETS = [
  { label: "Today", getRange: () => { const d = format(new Date(), "yyyy-MM-dd"); return [d, d]; } },
  { label: "This Week", getRange: () => [format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd"), format(new Date(), "yyyy-MM-dd")] },
  { label: "This Month", getRange: () => [format(startOfMonth(new Date()), "yyyy-MM-dd"), format(new Date(), "yyyy-MM-dd")] },
  { label: "Past 3 Months", getRange: () => [format(subMonths(new Date(), 3), "yyyy-MM-dd"), format(new Date(), "yyyy-MM-dd")] },
  { label: "Past 6 Months", getRange: () => [format(subMonths(new Date(), 6), "yyyy-MM-dd"), format(new Date(), "yyyy-MM-dd")] },
  { label: "This Year", getRange: () => [format(startOfYear(new Date()), "yyyy-MM-dd"), format(new Date(), "yyyy-MM-dd")] },
];

export const MODE_KPIS = [
  { key: "cash", label: "Cash", icon: Banknote, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  { key: "card", label: "Card", icon: CreditCard, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
  { key: "credit", label: "Credit", icon: Receipt, color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200" },
  { key: "upi", label: "UPI", icon: Smartphone, color: "text-violet-600", bg: "bg-violet-50", border: "border-violet-200" },
  { key: "eft", label: "EFT", icon: Landmark, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  { key: "neft_rtgs", label: "NEFT/RTGS", icon: Building, color: "text-cyan-600", bg: "bg-cyan-50", border: "border-cyan-200" },
  { key: "payout", label: "Payout", icon: ArrowDownLeft, color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
  { key: "others", label: "Others", icon: FileText, color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200" },
];
